/**
 * Messaggi di validazione in italiano — fonte unica di verità.
 *
 * Zod di default emette messaggi in inglese ("Too small: expected string to
 * have >=1 characters", "Invalid input: expected number, received NaN"): il
 * software è usato da staff italiano, quindi vanno in italiano ovunque, senza
 * scriverli a mano campo per campo.
 *
 * La strategia è un ERROR MAP GLOBALE impostato con `z.config()`: si definisce
 * qui, una volta sola, e OGNI schema — su web e su API — ne eredita i messaggi
 * in automatico. I messaggi specifici scritti sul singolo schema
 * (es. `z.email('Indirizzo email non valido')`) hanno comunque la precedenza.
 *
 * Per i casi non coperti qui si ricade sul locale italiano incluso in Zod
 * (`z.locales.it`), così non resta mai testo inglese.
 */
import * as z from 'zod';

/** Etichetta leggibile del tipo atteso, per i messaggi di `invalid_type`. */
function nomeTipo(atteso: string): string {
  switch (atteso) {
    case 'string':
      return 'testo';
    case 'number':
      return 'numero';
    case 'boolean':
      return 'valore vero/falso';
    case 'date':
      return 'data';
    case 'array':
      return 'elenco';
    default:
      return atteso;
  }
}

/** Unità corretta per i vincoli di dimensione (min/max) in base all'origine. */
function unita(origine: string | undefined, quantita: number): string {
  const plurale = quantita !== 1;
  switch (origine) {
    case 'string':
      return plurale ? 'caratteri' : 'carattere';
    case 'array':
    case 'set':
      return plurale ? 'elementi' : 'elemento';
    case 'number':
    case 'bigint':
    case 'date':
      return '';
    default:
      return plurale ? 'elementi' : 'elemento';
  }
}

const localeIt = z.locales.it().localeError;

/**
 * Error map con frasi curate per i casi che gli utenti incontrano di più;
 * per tutto il resto delega al locale italiano di Zod.
 */
const mappaErrori: z.core.$ZodErrorMap = (issue) => {
  switch (issue.code) {
    case 'invalid_type': {
      // Numero atteso ma ricevuto NaN: tipico del campo vuoto con coerce.
      if (
        (issue.expected === 'number' || issue.expected === 'int') &&
        Number.isNaN(issue.input as number)
      ) {
        return 'Inserisci un numero valido';
      }
      if (issue.input === undefined || issue.input === null) {
        return 'Campo obbligatorio';
      }
      if (issue.expected === 'int') {
        return 'Inserisci un numero intero';
      }
      return `Valore non valido: atteso un ${nomeTipo(issue.expected)}`;
    }

    case 'too_small': {
      const min = Number(issue.minimum);
      if (issue.origin === 'string' && min === 1) {
        return 'Campo obbligatorio';
      }
      const u = unita(issue.origin, min);
      const soglia = issue.inclusive ? 'almeno' : 'più di';
      return u
        ? `Inserisci ${soglia} ${min} ${u}`
        : `Il valore deve essere ${issue.inclusive ? 'almeno' : 'maggiore di'} ${min}`;
    }

    case 'too_big': {
      const max = Number(issue.maximum);
      const u = unita(issue.origin, max);
      const soglia = issue.inclusive ? 'al massimo' : 'meno di';
      return u
        ? `Inserisci ${soglia} ${max} ${u}`
        : `Il valore deve essere ${issue.inclusive ? 'al massimo' : 'minore di'} ${max}`;
    }

    case 'invalid_value': {
      // Enum / valore letterale non tra quelli ammessi.
      return 'Valore non ammesso';
    }

    default:
      // Email, formati, unione, chiavi non riconosciute, ecc.: locale italiano.
      return localeIt(issue);
  }
};

let configurato = false;

/**
 * Applica l'error map globale. Idempotente: importare il barrel di `shared`
 * la esegue una volta, sia sul bundle web sia sul processo API/worker.
 */
export function configuraMessaggiValidazione(): void {
  if (configurato) return;
  z.config({ customError: mappaErrori });
  configurato = true;
}

configuraMessaggiValidazione();
